import { useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { Reveal, Overline } from "@/components/Motion";
import { SEO } from "@/components/SEO";
import { ASSETS, SERVICES } from "@/lib/assets";

const IMGS = [ASSETS.cistern, ASSETS.curvedMarble, ASSETS.worldMap, ASSETS.goldMarble];

const Services = () => {
  const [hovered, setHovered] = useState(null);
  const [pos, setPos] = useState({ x: 0, y: 0 });

  return (
    <div data-testid="services-page" onMouseMove={(e) => setPos({ x: e.clientX, y: e.clientY })}>
      <SEO title="Exhibition Services — Stand Design, Build & Turnkey Solutions | EVO Exhibitions" description="Exhibition stand design, custom build, turnkey solutions, national pavilions, project management and staffing across Dubai and the Middle East." path="/services" />
      {/* Header */}
      <section className="px-6 md:px-12 lg:px-20 pt-40 md:pt-48 pb-20">
        <Reveal><Overline className="block mb-8">Capabilities</Overline></Reveal>
        <Reveal delay={0.1}>
          <h1 className="font-serif font-light text-5xl md:text-7xl lg:text-8xl tracking-tight leading-[0.95] text-evo-pearl max-w-4xl">
            Services engineered for the show floor.
          </h1>
        </Reveal>
        <Reveal delay={0.2}>
          <p className="mt-10 text-evo-ash font-light max-w-xl leading-relaxed">
            Nine disciplines, one accountable team. Every service below is delivered in-house or under our direct project management — the way premium exhibition work should be.
          </p>
        </Reveal>
      </section>

      {/* Service list rows */}
      <section className="px-6 md:px-12 lg:px-20 pb-32">
        <div className="border-t border-white/10">
          {SERVICES.map((s, i) => (
            <Reveal key={s.n} y={20}>
              <Link
                to={s.title.includes("Staffing") ? "/staffing" : "/contact"}
                data-testid={`service-row-${s.n}`}
                onMouseEnter={() => setHovered(i)}
                onMouseLeave={() => setHovered(null)}
                className="group grid grid-cols-12 items-center gap-4 py-8 md:py-10 border-b border-white/10 hover:border-evo-gold/30 transition-colors duration-500"
              >
                <span className="col-span-2 md:col-span-1 font-serif text-evo-gold/70 text-lg">{s.n}</span>
                <h2 className="col-span-9 md:col-span-6 font-serif text-2xl md:text-4xl text-evo-pearl group-hover:text-evo-gold group-hover:translate-x-2 transition-all duration-500">
                  {s.title}
                </h2>
                <p className="hidden md:block col-span-4 text-evo-ash text-sm font-light leading-relaxed">{s.desc}</p>
                <span className="col-span-1 flex justify-end">
                  <ArrowUpRight size={22} className="text-evo-ash/40 group-hover:text-evo-gold transition-all duration-300 group-hover:translate-x-1 group-hover:-translate-y-1" />
                </span>
              </Link>
            </Reveal>
          ))}
        </div>
      </section>

      {/* Cursor-following image */}
      <AnimatePresence>
        {hovered !== null && (
          <motion.div
            className="pointer-events-none fixed z-40 hidden lg:block w-72 h-96 overflow-hidden"
            style={{ left: pos.x + 30, top: pos.y - 200 }}
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.9 }}
            transition={{ duration: 0.3 }}
          >
            <img src={IMGS[hovered % IMGS.length]} alt="" className="w-full h-full object-cover" />
            <div className="absolute inset-0 border border-evo-gold/30" />
          </motion.div>
        )}
      </AnimatePresence>

      {/* CTA */}
      <section className="px-6 md:px-12 lg:px-20 pb-32">
        <div className="bg-evo-marble1 border border-white/5 p-10 md:p-16 flex flex-col md:flex-row md:items-center justify-between gap-8">
          <h2 className="font-serif font-light text-3xl md:text-5xl text-evo-pearl">
            Not sure where to start?
          </h2>
          <Link
            to="/contact"
            data-testid="services-cta-btn"
            className="inline-flex items-center gap-3 bg-evo-gold text-evo-black hover:bg-evo-goldsoft transition-colors duration-300 px-9 py-5 text-xs uppercase tracking-[0.2em] group shrink-0"
          >
            Request a Quotation
            <ArrowUpRight size={16} className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </Link>
        </div>
      </section>
    </div>
  );
};

export default Services;
